import React, { useState, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, CreditCardIcon, LandmarkIcon, SmartphoneIcon, PenLineIcon, LinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { MALAYSIAN_STATES, MALAYSIAN_BANKS, mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierForm() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get('id');
  const isEditing = !!supplierId;
  const editingSupplier = isEditing ? mockSuppliers.find(s => s.id === supplierId) || null : null;
  const [entryMethod, setEntryMethod] = useState<'manual' | 'invite'>('manual');
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>(editingSupplier?.supplierType || 'company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [formData, setFormData] = useState({
    // Shared
    email: editingSupplier?.email || '',
    phone: editingSupplier?.phone || '',
    useAsBillingEmail: editingSupplier?.useAsBillingEmail ?? true,
    addressLine1: editingSupplier?.addressLine1 || '',
    addressLine2: editingSupplier?.addressLine2 || '',
    city: editingSupplier?.city || '',
    province: editingSupplier?.province || '',
    country: 'Maldives',
    postcode: editingSupplier?.postcode || '',
    internalReference: editingSupplier?.internalReference || '',
    // Company
    taxId: editingSupplier?.taxId || '',
    companyNameTh: editingSupplier?.companyNameTh || '',
    companyNameEn: editingSupplier?.companyNameEn || '',
    branch: editingSupplier?.branch || '00000',
    vatRegistered: editingSupplier?.vatRegistered ?? false,
    contactPerson: editingSupplier?.contactPerson || '',
    // Individual
    idType: editingSupplier?.idType || 'citizen_id',
    idNumber: editingSupplier?.idNumber || '',
    fullNameTh: editingSupplier?.fullNameTh || '',
    fullNameEn: editingSupplier?.fullNameEn || '',
    individualTaxId: editingSupplier?.individualTaxId || '',
    // Pay-in method
    payInMethod: editingSupplier?.payInMethod || 'acquiring',
    cardholderName: editingSupplier?.cardholderName || '',
    cardNumber: editingSupplier?.cardNumber || '',
    expiryDate: editingSupplier?.expiryDate || '',
    // Payout method
    payoutMethod: editingSupplier?.payoutMethod || 'bank_account',
    bankName: editingSupplier?.bankName || '',
    accountNumber: editingSupplier?.accountNumber || '',
    accountName: editingSupplier?.accountName || '',
    promptPayType: editingSupplier?.promptPayType || 'phone',
    promptPayId: editingSupplier?.promptPayId || '',
    promptPayName: editingSupplier?.promptPayName || ''
  });
  // DBD lookup state for API response metadata
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [copied, setCopied] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [linkGenerated, setLinkGenerated] = useState(false);
  const inviteLink = `${window.location.origin}/supplier-onboarding/abc123def`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        country: 'Maldives',
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: 'Test Company Limited',
        companyNameEn: 'Test Company Limited',
        addressLine1: '123/45 Boduthakurufaanu Magu',
        addressLine2: 'Henveiru',
        city: 'Malé',
        country: 'Maldives',
        postcode: '20094'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/suppliers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-12989">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-12990">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-12991">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-12992" />
          {t('pages.BulkSupplierUpload.back_to_suppliers')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12993">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-12994">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-12995">
            {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-12996">
          {/* SECTION 1: Supplier Information */}
          <div className="space-y-6" data-id="element-12997">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-12998">
              {t('pages.SupplierForm.supplier_information')}
            </h2>

            {/* Entry Method Selection */}
            {!isEditing && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12999">
                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'manual' ? 'border-[#E2231A] ring-1 ring-[#E2231A] bg-red-50/30' : 'border-gray-200'}`} data-id="element-13000">
                  <input type="radio" name="entryMethod" value="manual" checked={entryMethod === 'manual'} onChange={() => setEntryMethod('manual')} className="sr-only" data-id="element-13001" />

                  <div className="flex w-full items-start" data-id="element-13002">
                    <div className="flex items-center h-5" data-id="element-13003">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'manual' ? 'border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13004">
                        {entryMethod === 'manual' && <div className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-13005" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-13006">
                      <div className="flex items-center gap-2 mb-1" data-id="element-13007">
                        <PenLineIcon size={16} className={entryMethod === 'manual' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-13008" />

                        <span className={`text-sm font-medium ${entryMethod === 'manual' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-13009">
                          {t('pages.SupplierForm.enter_details_myself')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-13010">
                        {t('pages.SupplierForm.fill_in_supplier_identity_contact_and')}
                      </p>
                    </div>
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'invite' ? 'border-[#E2231A] ring-1 ring-[#E2231A] bg-red-50/30' : 'border-gray-200'}`} data-id="element-13011">
                  <input type="radio" name="entryMethod" value="invite" checked={entryMethod === 'invite'} onChange={() => setEntryMethod('invite')} className="sr-only" data-id="element-13012" />

                  <div className="flex w-full items-start" data-id="element-13013">
                    <div className="flex items-center h-5" data-id="element-13014">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'invite' ? 'border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13015">
                        {entryMethod === 'invite' && <div className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-13016" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-13017">
                      <div className="flex items-center gap-2 mb-1" data-id="element-13018">
                        <LinkIcon size={16} className={entryMethod === 'invite' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-13019" />

                        <span className={`text-sm font-medium ${entryMethod === 'invite' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-13020">
                          {t('pages.SupplierForm.invite_supplier')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-13021">
                        {t('pages.SupplierForm.generate_a_secure_link_for_the')}
                      </p>
                    </div>
                  </div>
                </label>
              </div>}

            {/* Supplier Type Toggle (Always visible) */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-13022">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13023">
                <BuildingIcon size={16} className="mr-2" data-id="element-13024" />
                {t('pages.SupplierDetail.company')}
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13025">
                <UserIcon size={16} className="mr-2" data-id="element-13026" />
                {t('pages.SupplierDetail.individual')}
              </button>
            </div>

            {/* MANUAL ENTRY FLOW */}
            {entryMethod === 'manual' && <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-13027">
                {/* --- COMPANY FIELDS --- */}
                {supplierType === 'company' && <div className="space-y-6" data-id="element-13028">
                    {/* DBD Lookup Section */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-13029">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13030">
                        {t('pages.SupplierForm.tax_id_registration_number')}
                      </label>
                      <div className="flex gap-3" data-id="element-13031">
                        <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.13digit_registration_number')} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13032" />

                        <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-[#E2231A]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13033">
                          {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-13034" /> : <>
                              <SearchIcon size={18} className="mr-2" data-id="element-13035" />
                              {t('pages.SupplierForm.lookup')}
                            </>}
                        </button>
                      </div>

                      {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-13036">
                          {lookupError}
                        </p>}

                      {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-13037">
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start" data-id="element-13038">
                            <CheckCircle2Icon size={20} className="text-green-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-13039" />

                            <div data-id="element-13040">
                              <p className="text-sm font-medium text-green-800" data-id="element-13041">
                                {t('pages.SupplierForm.company_details_fetched_from_department_of')}
                              </p>
                              <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-green-700" data-id="element-13042">
                                {dbdMeta.registerDate && <>
                                    <span data-id="element-13043">
                                      {t('pages.SupplierForm.registered')}{' '}
                                      {dbdMeta.registerDate}
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-13044"></span>
                                  </>}
                                {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-13045">
                                    {dbdMeta.juristicStatus}
                                  </span>}
                                {dbdMeta.juristicType && <>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-13046"></span>
                                    <span data-id="element-13047">{dbdMeta.juristicType}</span>
                                  </>}
                              </div>
                            </div>
                          </div>
                          {dbdMeta.isMockFallback && <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-13048">
                              <p className="text-xs text-amber-700" data-id="element-13049">
                                <span className="font-medium" data-id="element-13050">
                                  {t('pages.SupplierForm.note')}
                                </span>{' '}
                                {t('pages.SupplierForm.direct_browser_request_to_dbd_api')}
                              </p>
                            </div>}
                        </div>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13051">
                      <div data-id="element-13052">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13053">
                          {t('pages.SupplierForm.company_name_local')}
                        </label>
                        <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13054" />
                      </div>
                      <div data-id="element-13055">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13056">
                          {t('pages.SupplierDetail.company_name_english')}
                        </label>
                        <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13057" />
                      </div>
                      <div data-id="element-13058">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13059">
                          {t('pages.SupplierForm.branch_number')}
                        </label>
                        <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13060" />
                      </div>
                      <div data-id="element-13061">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13062">
                          {t('pages.SupplierForm.contact_person_name')}
                        </label>
                        <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13063" />
                      </div>
                    </div>

                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-13064">
                      <div className="relative flex items-center justify-center" data-id="element-13065">
                        <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-13066" />

                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-13067"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-13068">
                          <polyline points="20 6 9 17 4 12" data-id="element-13069"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-13070">
                        {t('pages.SupplierForm.vat_registered_company')}
                      </span>
                    </label>
                  </div>}

                {/* --- INDIVIDUAL FIELDS --- */}
                {supplierType === 'individual' && <div className="space-y-6" data-id="element-13071">
                    <div className="flex gap-6 mb-2" data-id="element-13072">
                      <label className="flex items-center cursor-pointer" data-id="element-13073">
                        <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13074" />

                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-13075">
                          {t('pages.SupplierForm.thai_citizen_id')}
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer" data-id="element-13076">
                        <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13077" />

                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-13078">
                          {t('pages.SupplierForm.passport')}
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13079">
                      <div className="md:col-span-2" data-id="element-13080">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13081">
                          {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                        </label>
                        <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13082" />
                      </div>
                      <div data-id="element-13083">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13084">
                          {t('pages.SupplierForm.full_name_local')}
                        </label>
                        <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13085" />
                      </div>
                      <div data-id="element-13086">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13087">
                          {t('pages.SupplierForm.full_name')}
                        </label>
                        <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13088" />
                      </div>
                      <div className="md:col-span-2" data-id="element-13089">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13090">
                          {t('pages.SupplierForm.tax_id_if_different_from_citizen')}
                        </label>
                        <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13091" />
                      </div>
                    </div>
                  </div>}

                {/* --- SHARED CONTACT FIELDS --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-13092">
                  <div data-id="element-13093">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13094">
                      {t('pages.SupplierForm.email_address')}
                    </label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_email_address')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13095" />
                  </div>
                  <div data-id="element-13096">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13097">
                      {t('pages.SupplierForm.phone_number')}
                    </label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_0812345678')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13098" />
                  </div>
                  <div className="md:col-span-2" data-id="element-13099">
                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-13100">
                      <div className="relative flex items-center justify-center" data-id="element-13101">
                        <input type="checkbox" name="useAsBillingEmail" checked={formData.useAsBillingEmail} onChange={handleChange} className="peer sr-only" data-id="element-13102" />

                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-13103"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-13104">
                          <polyline points="20 6 9 17 4 12" data-id="element-13105"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-13106">
                        {t('pages.SupplierForm.use_this_email_as_the_billing')}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Divider: Billing information */}
                <div className="relative py-2" data-id="element-13107">
                  <div className="absolute inset-0 flex items-center" data-id="element-13108">
                    <div className="w-full border-t border-gray-200" data-id="element-13109"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-13110">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13111">
                      {t('pages.SupplierForm.billing_information')}
                    </span>
                  </div>
                </div>

                {/* Billing Info Fields */}
                <div className="space-y-5" data-id="element-13112">
                  <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_1')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13113" />

                  <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_2')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13114" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13115">
                    <div data-id="element-13116">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-13117">
                        {t('pages.SupplierForm.city_district')}
                      </label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_khlong_toei')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13118" />
                    </div>
                    <div data-id="element-13119">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-13120">
                        {t('pages.BulkSupplierUpload.province')}
                      </label>
                      <SearchableSelect name="province" options={MALAYSIAN_STATES} value={formData.province} onChange={val => setFormData(prev => ({
                    ...prev,
                    province: val
                  }))} placeholder={t('pages.SupplierForm.attrs.select_province')} data-id="element-13121" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13122">
                    <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-13123">
                      {t('pages.SupplierForm.thailand')}
                    </div>
                    <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.postcode_5_digits')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13124" />
                  </div>
                </div>

                {/* Divider: Additional information */}
                <div className="relative py-2" data-id="element-13125">
                  <div className="absolute inset-0 flex items-center" data-id="element-13126">
                    <div className="w-full border-t border-gray-200" data-id="element-13127"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-13128">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13129">
                      {t('pages.SupplierForm.additional_information')}
                    </span>
                  </div>
                </div>

                {/* Additional Info Fields */}
                <div data-id="element-13130">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13131">
                    {t('pages.SupplierDetail.internal_reference')}
                  </label>
                  <input type="text" name="internalReference" value={formData.internalReference} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_internal_reference')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13132" />
                </div>

                {/* Divider: Payout method */}
                <div className="relative py-2" data-id="element-13133">
                  <div className="absolute inset-0 flex items-center" data-id="element-13134">
                    <div className="w-full border-t border-gray-200" data-id="element-13135"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-13136">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13137">
                      {t('pages.SupplierDetail.payout_method')}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 -mt-4" data-id="element-13138">
                  {t('pages.SupplierForm.choose_how_this_supplier_will_receive')}
                </p>

                {/* Payout Method Selection */}
                <div className="space-y-4" data-id="element-13139">
                  {/* Bank Account Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13140">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13141">
                      <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13142" />

                      <div className="ml-3 flex items-center" data-id="element-13143">
                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-13144">
                          <LandmarkIcon size={16} className="text-green-600" data-id="element-13145" />
                        </div>
                        <div data-id="element-13146">
                          <div className="text-sm font-medium text-gray-900" data-id="element-13147">
                            {t('pages.SupplierDetail.bank_account')}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-13148">
                            {t('pages.SupplierForm.direct_transfer_to_a_thai_bank')}
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-13149">
                        <div data-id="element-13150">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13151">
                            {t('pages.SupplierDetail.bank_name')}
                          </label>
                          <SearchableSelect name="bankName" options={MALAYSIAN_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                      ...prev,
                      bankName: val
                    }))} placeholder={t('pages.SupplierForm.attrs.select_a_bank')} data-id="element-13152" />
                        </div>
                        <div data-id="element-13153">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13154">
                            {t('pages.SupplierDetail.account_number')}
                          </label>
                          <input type="text" name="accountNumber" maxLength={12} placeholder={t('pages.SupplierForm.attrs.1012_digits')} value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13155" />
                        </div>
                        <div data-id="element-13156">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13157">
                            {t('pages.SupplierDetail.account_name')}
                          </label>
                          <input type="text" name="accountName" placeholder={t('pages.SupplierForm.attrs.name_on_the_bank_account')} value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13158" />
                        </div>
                      </div>}
                  </div>

                  {/* PromptPay Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13159">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13160">
                      <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13161" />

                      <div className="ml-3 flex items-center" data-id="element-13162">
                        <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-13163">
                          <SmartphoneIcon size={16} className="text-purple-600" data-id="element-13164" />
                        </div>
                        <div data-id="element-13165">
                          <div className="text-sm font-medium text-gray-900" data-id="element-13166">
                            {t('pages.SupplierDetail.promptpay')}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-13167">
                            {t('pages.SupplierForm.fast_transfer_via_phone_citizen_id')}
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-13168">
                        <div data-id="element-13169">
                          <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-13170">
                            {t('pages.SupplierForm.promptpay_type')}
                          </label>
                          <div className="flex flex-wrap gap-4" data-id="element-13171">
                            <label className="flex items-center" data-id="element-13172">
                              <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13173" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-13174">
                                {t('pages.SupplierForm.phone_number_2')}
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-13175">
                              <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13176" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-13177">
                                {t('pages.SupplierForm.citizen_id')}
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-13178">
                              <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13179" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-13180">
                                {t('pages.BulkSupplierUpload.tax_id')}
                              </span>
                            </label>
                          </div>
                        </div>
                        <div data-id="element-13181">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13182">
                            {t('pages.SupplierDetail.promptpay_id')}
                          </label>
                          <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13183" />
                        </div>
                        <div data-id="element-13184">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13185">
                            {t('pages.SupplierForm.display_name_for_verification')}
                          </label>
                          <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13186" />
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}

            {/* INVITE SUPPLIER FLOW */}
            {entryMethod === 'invite' && <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-13187">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6" data-id="element-13188">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-13189">
                    {t('pages.SupplierForm.invite_supplier_2')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-13190">
                    {t('pages.SupplierForm.enter_a_name_for_this_supplier')}
                  </p>

                  <div className="space-y-4" data-id="element-13191">
                    <div data-id="element-13192">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-13193">
                        {t('pages.SupplierForm.supplier_name')}
                      </label>
                      <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder={t('pages.SupplierForm.attrs.eg_siam_fresh_market')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13194" />
                    </div>

                    {!linkGenerated && <button type="button" onClick={() => {
                  if (inviteName.trim()) setLinkGenerated(true);
                }} disabled={!inviteName.trim()} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13195">
                        <LinkIcon size={16} className="mr-2" data-id="element-13196" />
                        {t('pages.SupplierForm.generate_invite_link')}
                      </button>}

                    {linkGenerated && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-13197">
                        <div data-id="element-13198">
                          <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-13199">
                            {t('pages.SupplierForm.secure_link')}
                          </label>
                          <div className="flex gap-2" data-id="element-13200">
                            <input type="text" readOnly value={inviteLink} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-600 font-mono" data-id="element-13201" />

                            <button type="button" onClick={handleCopyLink} className="flex items-center justify-center px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors shadow-sm min-w-[100px]" data-id="element-13202">
                              {copied ? <>
                                  <CheckIcon size={16} className="mr-2 text-green-600" data-id="element-13203" />{' '}
                                  {t('pages.SupplierForm.copied')}
                                </> : <>
                                  <CopyIcon size={16} className="mr-2" data-id="element-13204" />{' '}
                                  {t('pages.SupplierForm.copy')}
                                </>}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-13205">
                          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" data-id="element-13206"></div>
                          <span className="text-sm font-medium text-amber-800" data-id="element-13207">
                            {t('pages.SupplierForm.pending_waiting_for_supplier_to_complete')}
                          </span>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}
          </div>

          {/* SECTION 2: Funds Source (Pay-in) - ALWAYS VISIBLE */}
          <div className="space-y-6 pt-8 border-t border-gray-200" data-id="element-13208">
            <div data-id="element-13209">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-13210">
                {t('pages.SupplierForm.funds_source_payin')}
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-13211">
                {t('pages.SupplierForm.choose_how_you_will_fund_payments')}
              </p>
            </div>

            {/* Pay-in Method Selection */}
            <div className="space-y-4" data-id="element-13212">
              {/* Acquiring Volume Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'acquiring' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13213">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13214">
                  <input type="radio" name="payInMethod" value="acquiring" checked={formData.payInMethod === 'acquiring'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13215" />

                  <div className="ml-3 flex items-center" data-id="element-13216">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-13217">
                      <LandmarkIcon size={16} className="text-green-600" data-id="element-13218" />
                    </div>
                    <div data-id="element-13219">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13220">
                        {t('pages.SupplierForm.acquiring_volume')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-13221">
                        {t('pages.SupplierForm.use_your_merchant_acquiring_balance_funds')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'acquiring' && <div className="p-4 bg-gray-50 border-t border-gray-100 rounded-b-xl" data-id="element-13222">
                    <p className="text-xs text-gray-600" data-id="element-13223">
                      {t('pages.SupplierForm.supplier_payments_will_be_deducted_from')}
                    </p>
                  </div>}
              </div>

              {/* Card Payment Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'card' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13224">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13225">
                  <input type="radio" name="payInMethod" value="card" checked={formData.payInMethod === 'card'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13226" />

                  <div className="ml-3 flex items-center" data-id="element-13227">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-3" data-id="element-13228">
                      <CreditCardIcon size={16} className="text-blue-600" data-id="element-13229" />
                    </div>
                    <div data-id="element-13230">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13231">
                        {t('pages.SupplierForm.card_payment')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-13232">
                        {t('pages.SupplierForm.link_your_own_debit_or_credit')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'card' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-13233">
                    <p className="text-xs text-gray-500" data-id="element-13234">
                      {t('pages.SupplierForm.your_card_will_be_charged_each')}
                    </p>
                    <div data-id="element-13235">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13236">
                        {t('pages.SupplierForm.name_on_card')}
                      </label>
                      <input type="text" name="cardholderName" placeholder={t('pages.SupplierForm.attrs.your_name_as_it_appears_on')} value={formData.cardholderName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13237" />
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-13238">
                      <div className="col-span-2" data-id="element-13239">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13240">
                          {t('pages.SupplierDetail.card_number')}
                        </label>
                        <input type="text" name="cardNumber" placeholder={t('pages.SupplierForm.attrs.xxxx_xxxx_xxxx_xxxx')} value={formData.cardNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13241" />
                      </div>
                      <div data-id="element-13242">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13243">
                          {t('pages.SupplierForm.expiry_date')}
                        </label>
                        <input type="text" name="expiryDate" placeholder={t('pages.SupplierForm.attrs.mmyy')} value={formData.expiryDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13244" />
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100" data-id="element-13245">
            <button type="button" onClick={() => navigate('/suppliers')} className="px-6 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-13246">
              {t('pages.BulkSupplierUpload.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13247">
              {t('pages.SupplierForm.save_supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}